/**
 * Types for Broadcast Queue (очередь рассылок с ручным подтверждением)
 * Дата: 13.02.2026
 * Обновлено: 10.04.26 — добавлены типы async broadcast operation job/progress
 * Связан с: internal/domain/entity/broadcast_queue.go, internal/domain/entity/broadcast_operation_job.go
 */

import { DELIVERY_CHANNEL_IDS, getChannelDisplayName, getChannelIcon } from '@/config/channels';
export { QUEUE_SOURCE_LABELS as eventTypeLabels } from '@/features/broadcast-queue/queueSourceRegistry';
export { getQueueSourceLabelForQueueItem } from '@/features/broadcast-queue/queueSourceRegistry';

export type BroadcastQueueStatus =
  | 'pending_approval'
  | 'approved'
  | 'sent'
  | 'rejected'
  | 'expired'
  | 'failed';

export type BroadcastMode = 'manual' | 'auto';

export type BroadcastOperationJobStatus =
  | 'queued'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'partial';

export interface BroadcastOperationJobRecentItem {
  queue_item_id: string;
  patient_name?: string;
  phone: string;
  channel?: string;
  status: BroadcastOperationJobStatus;
  error?: string;
  processed_at: string;
}

export interface BroadcastOperationJob {
  id: string;
  tenant_id: string;
  operation: string;
  status: BroadcastOperationJobStatus;
  total_items: number;
  processed_items: number;
  sent_items: number;
  failed_items: number;
  queue_item_ids: string[];
  recent_items: BroadcastOperationJobRecentItem[];
  current_item_id?: string;
  current_patient_name?: string;
  current_phone?: string;
  current_channel?: string;
  last_error?: string;
  message?: string;
  started_by?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface StartBroadcastOperationJobResponse {
  job_id: string;
  job: BroadcastOperationJob;
  message: string;
}

export interface BroadcastQueueItem {
  id: string;
  tenant_id: string;
  patient_id?: string;
  event_type: string;
  phone: string;
  patient_name?: string;
  available_channels?: {
    whatsapp_waba?: boolean;
    whatsapp_regular?: boolean;
    telegram_bot?: boolean;
    telegram_client?: boolean;
    max?: boolean;
    max_client?: boolean;
    sms?: boolean;
    sms_tel?: boolean;
    phone?: boolean;
  };
  template_id?: string;
  template_name?: string;
  rendered_text?: string;
  variables: Record<string, string>;
  metadata: Record<string, unknown>;
  channel?: string;
  status: BroadcastQueueStatus;
  constraint_results: ConstraintResult[];
  approved_by?: string;
  approved_at?: string;
  reject_reason?: string;
  sent_at?: string;
  send_result?: string;
  external_id?: string;
  scheduled_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ConstraintResult {
  name: string;
  allowed: boolean;
  reason?: string;
}

export interface BroadcastQueueSummary {
  tenant_id: string;
  pending_approval: number;
  approved: number;
  sent: number;
  rejected: number;
  expired: number;
  failed: number;
  total: number;
}

export interface BroadcastQueueListResponse {
  items: BroadcastQueueItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface BroadcastQueueFilter {
  status?: BroadcastQueueStatus;
  event_type?: string;
  phone?: string;
  created_from?: string;
  created_to?: string;
  event_date_from?: string;
  event_date_to?: string;
  semantic_dates?: boolean;
  offset?: number;
  limit?: number;
}

export interface BroadcastModeResponse {
  mode: BroadcastMode;
  description: string;
  broadcasting_enabled?: boolean;
  warnings?: BroadcastModeWarning[];
}

export interface BroadcastModeWarning {
  code: string;
  severity: 'warning' | 'critical' | 'info';
  message: string;
  affected_items?: number;
  affected_templates?: string[];
}

// ═══════════════════════════════════════════════════════════════════════
// Delivery Preview (14.02.2026)
// ═══════════════════════════════════════════════════════════════════════

/** Статус доступности канала для пациента */
export interface ChannelPreviewStatus {
  available: boolean;  // Есть привязка / канал настроен
  connected: boolean;  // Канал активен (подключён)
  disabled: boolean;   // В списке disabled_channels
  reason: string;      // Пояснение
}

/** Шаг в цепочке fallback */
export interface FallbackStep {
  channel: string;
  name: string;
  available: boolean;
  would_use: boolean;   // Это resolved канал
  reason: string;
}

/** Кнопка шаблона (TG Bot inline keyboard) */
export interface TemplateButton {
  text: string;
  callback_data: string;
  type: 'quick_reply' | 'url' | 'phone_number';
  url?: string;
}

/** Полный предпросмотр доставки элемента очереди */
export interface DeliveryPreview {
  // Информация о шаблоне
  template_id?: string;
  template_name: string;
  template_type: string;
  is_active: boolean;

  // Отрендеренные тексты
  rendered_content: string;          // Content с переменными (TG Bot)
  rendered_text_alternative: string; // TextAlternative (TG Client, SMS)
  buttons?: TemplateButton[];

  // Результат резолвинга канала
  resolved_channel: string;
  resolved_channel_name: string;
  resolved_channel_reason: string;
  patient_preferred_channel?: string;

  // Доступность каналов для пациента
  patient_channels: Record<string, ChannelPreviewStatus>;

  // Настройки тенанта
  channel_priority: string[];
  disabled_channels: string[];

  // Визуальная цепочка fallback
  fallback_chain: FallbackStep[];

  // Финальный текст, который будет отправлен
  final_message_text: string;
  preview_action_url?: string;
  preview_action_urls?: string[];
}

/** Ответ batch-preview */
export interface BatchPreviewResponse {
  previews: Record<string, DeliveryPreview>;
  total: number;
}

// ═══════════════════════════════════════════════════════════════════════
// UI helpers
// ═══════════════════════════════════════════════════════════════════════

/** Человекочитаемые имена каналов */
export const channelDisplayNames: Record<string, string> = Object.fromEntries([
  ...DELIVERY_CHANNEL_IDS.map((channel) => [channel, getChannelDisplayName(channel)]),
  ['none', getChannelDisplayName('none')],
]) as Record<string, string>;

/** Иконки каналов */
export const channelIcons: Record<string, string> = Object.fromEntries([
  ...DELIVERY_CHANNEL_IDS.map((channel) => [channel, getChannelIcon(channel)]),
  ['none', getChannelIcon('none')],
]) as Record<string, string>;

export const statusLabels: Record<BroadcastQueueStatus, string> = {
  pending_approval: 'Ожидает одобрения',
  approved: 'Одобрено',
  sent: 'Отправлено',
  rejected: 'Отклонено',
  expired: 'Просрочено',
  failed: 'Ошибка',
};

export const statusColors: Record<BroadcastQueueStatus, string> = {
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-500',
  failed: 'bg-red-100 text-red-700',
};

export const broadcastOperationStatusLabels: Record<BroadcastOperationJobStatus, string> = {
  queued: 'В очереди',
  in_progress: 'В процессе',
  completed: 'Завершено',
  failed: 'Ошибка',
  partial: 'Частично',
};

export type BroadcastQueueDispatchMode = 'auto' | 'manual' | 'unknown';

export function getBroadcastQueueDispatchMode(item: BroadcastQueueItem): BroadcastQueueDispatchMode {
  if (item.approved_at) {
    return 'manual';
  }

  const metadataDispatchMode = item.metadata?.dispatch_mode;
  if (metadataDispatchMode === 'auto' || metadataDispatchMode === 'manual') {
    return metadataDispatchMode;
  }

  return 'unknown';
}

export function getBroadcastQueueStatusDetail(item: BroadcastQueueItem): string {
  const dispatchMode = getBroadcastQueueDispatchMode(item);

  if (item.status === 'sent') {
    if (dispatchMode === 'auto') {
      return 'Отправлено автоматически';
    }
    if (dispatchMode === 'manual') {
      return 'Отправлено вручную';
    }
  }

  if (item.status === 'failed') {
    if (dispatchMode === 'auto') {
      return 'Ошибка автоматической отправки';
    }
    if (dispatchMode === 'manual') {
      return 'Ошибка ручной отправки';
    }
  }

  return '';
}

// ═══════════════════════════════════════════════════════════════════════
// Channel Switch (12.03.2026)
// ═══════════════════════════════════════════════════════════════════════

export interface ChangeChannelResponse {
  success: boolean;
  message: string;
  item?: BroadcastQueueItem;
  new_channel?: string;
  old_channel?: string;
  template_id?: string;
  error?: string;
  constraints?: string[];
}

export interface ChannelOption {
  channel: string;
  display_name: string;
  available: boolean;
  current: boolean;
  reason?: string;
  change_available?: boolean;
  change_reason?: string;
  test_available?: boolean;
  test_reason?: string;
  test_target_label?: string;
  test_target_value?: string;
}

export interface AvailableChannelsResponse {
  item_id: string;
  current_channel: string;
  channels: ChannelOption[];
}

export interface BulkChangeChannelResult {
  item_id: string;
  phone: string;
  patient_name: string;
  event_type?: string;
  status?: string;
  success: boolean;
  old_channel?: string;
  new_channel?: string;
  error_code?: string;
  error?: string;
}

export interface BulkChangeChannelReasonSummary {
  error_code: string;
  reason: string;
  count: number;
  patients?: string[];
}

export interface BulkChangeChannelResponse {
  total: number;
  success: number;
  failed: number;
  results: BulkChangeChannelResult[];
  failed_items?: BulkChangeChannelResult[];
  reason_summary?: BulkChangeChannelReasonSummary[];
  failure_summary?: string;
  message: string;
  skipped_patients?: string[];
  skipped_message?: string;
}

export interface SendTestCloneItemResult {
  success: boolean;
  message: string;
  error?: string;
  clone_item_id?: string;
  clone_status?: BroadcastQueueStatus;
  target_label?: string;
  target_value?: string;
  target_channel?: string;
  item?: BroadcastQueueItem;
}

export interface SendTestCloneResponse {
  success: boolean;
  message: string;
  error?: string;
  clone_item_id?: string;
  clone_status?: BroadcastQueueStatus;
  target_label?: string;
  target_value?: string;
  target_channel?: string;
  item?: BroadcastQueueItem;
  results?: SendTestCloneItemResult[];
}
